// Facebook Instagram Integration Registration
// Registers the Facebook/Instagram integration with the integration registry

import { IntegrationRegistry } from '../registry/IntegrationRegistry';
import { FacebookInstagramIntegrationFactory, FacebookInstagramConfig } from './FacebookInstagramIntegrationFactory';
import { IntegrationConfig } from '../../../types/integration';

/**
 * Register Facebook/Instagram integration with the integration registry
 */
export function registerFacebookInstagramIntegration(): void {
  const registry = IntegrationRegistry.getInstance();
  
  // Create a factory object that matches the expected interface
  const factory = {
    create: async (config: IntegrationConfig) => {
      // Convert IntegrationConfig to FacebookInstagramConfig
      const fbConfig = config as unknown as FacebookInstagramConfig;
      return await FacebookInstagramIntegrationFactory.create(fbConfig);
    },
    
    validate: async (config: Partial<IntegrationConfig>) => {
      const validation = validateFacebookInstagramConfig(config);
      return {
        valid: validation.valid,
        errors: validation.errors.map(error => ({ field: 'config', message: error, code: 'VALIDATION_ERROR' })),
        warnings: []
      };
    },
    
    getConfigSchema: () => ({
      type: 'object',
      required: ['credentials'],
      properties: {
        credentials: {
          type: 'object',
          required: ['accessToken', 'appId', 'appSecret'],
          properties: {
            accessToken: { type: 'string' },
            appId: { type: 'string' },
            appSecret: { type: 'string' }
          }
        }
      }
    }),
    
    getSupportedFeatures: () => [
      'post_publishing',
      'post_scheduling',
      'engagement_tracking',
      'audience_creation',
      'lookalike_audiences',
      'cross_channel_coordination',
      'real_time_sync'
    ]
  };

  registry.register(
    'facebook_instagram',
    'Facebook & Instagram',
    'social' as any,
    'facebook_instagram',
    '1.0.0',
    factory
  );
}

/**
 * Get Facebook/Instagram integration metadata
 */
export function getFacebookInstagramIntegrationMetadata() {
  const registry = IntegrationRegistry.getInstance();
  return registry.getRegistration('facebook_instagram');
}

/**
 * Validate Facebook/Instagram integration configuration
 */
export function validateFacebookInstagramConfig(config: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!config.credentials) {
    errors.push('Credentials are required');
  } else {
    if (!config.credentials.accessToken) {
      errors.push('Access token is required');
    }
    if (!config.credentials.appId) {
      errors.push('App ID is required');
    }
    if (!config.credentials.appSecret) {
      errors.push('App secret is required');
    }
  }
  
  if (config.settings) {
    if (!config.settings.enableFacebook && !config.settings.enableInstagram) {
      errors.push('At least one platform (Facebook or Instagram) must be enabled');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get setup instructions for Facebook/Instagram integration
 */
export function getFacebookInstagramSetupInstructions(): string[] {
  return [
    '1. Create a Facebook App in Facebook Developer Console',
    '2. Add Facebook Login and Instagram Basic Display products',
    '3. Configure OAuth redirect URIs',
    '4. Generate a Page Access Token with required permissions:',
    '   - pages_manage_posts',
    '   - pages_read_engagement', 
    '   - instagram_basic',
    '   - instagram_content_publish',
    '5. Connect Instagram Business Account to Facebook Page',
    '6. Copy App ID, App Secret, and Page Access Token',
    '7. Use the setup wizard to complete integration'
  ];
}